import React from 'react'
import { Button, Typography } from 'antd'
import { IconItem } from '@components'

import { 
  BookOutlined, 
  CalendarOutlined,
  CheckCircleOutlined,
  CheckCircleFilled, 
  CloseCircleFilled, 
  FormOutlined, 
  PlusCircleOutlined,
  UserOutlined 
} from '@ant-design/icons'

import {
  getFormattedSessionDateRange,
  getGroupLessonNote,
  getLessonSubject,
  getLocationName,
  getStudentFullname,
  hasSessionExpired
} from '@helpers'

import { meetingTypes } from '@config'
import PropTypes from 'prop-types'

import './InvitationCard.less'

const { Paragraph, Title } = Typography

const InvitationCard = ({ 
  event, 
  invitation, 
  invitationGroup, 
  isWaitingListEnabled,
  handleInvitationGroupChange, 
  handleShowWaitList,
  joinedWaitinglist,
  session 
}) => {
  const { lesson, group, student } = invitationGroup
  const { groupLessonNotes, location } = group
  const { availableSlotCount } = group
  const sessionDateRange = getFormattedSessionDateRange(session.starts_at, session.ends_at)
  const lessonSubject = getLessonSubject(lesson)
  const studentName = getStudentFullname(student)
  const locationName = getLocationName(invitation, location)
  const groupLessonNote = getGroupLessonNote(groupLessonNotes, lesson)
  const hasExpired = hasSessionExpired(session.ends_at, 12)
  const isDisabled = event.disable_parent_bookings
  const showBookButton = !hasExpired && !isDisabled

  const joinWaitListButton = (isMobile) => (
    <Button 
      className={`sb-invitation-card__waitlist-button ${joinedWaitinglist ? 'sb-invitation-card__waitlist-button--joined' : ''} ${isMobile ? 'sb-invitation-card__waitlist-button--mobile' : ''}`} 
      type='primary' 
      shape='round' 
      size='large' 
      onClick={() => handleShowWaitList(invitationGroup)}
      block={isMobile}
    >
      {joinedWaitinglist 
        ? <span className="flex align-center justify-center gap-2"><CheckCircleOutlined className="sb-invitation-card__waitlist-icon" /> Manage Waiting List</span>
        : <span className="flex align-center justify-center gap-2"><PlusCircleOutlined className="sb-invitation-card__waitlist-icon" /> Join Waiting List</span>
      }
    </Button>
  )

  return (
    <div className='sb-invitation-card'>
      <div className='sb-invitation-card__heading'>
        <div className='sb-invitation-card__title'>
          <Title level={4}>{group.title}</Title>
        </div>

        {/** Book button for screens > 480px */}
        <div className='sb-invitation-card__controls'>
          {showBookButton && availableSlotCount === 0 && isWaitingListEnabled && joinWaitListButton(false)}

          {showBookButton && availableSlotCount > 0 && (
            <Button
              className='sb-invitation-card__book-button'
              type='primary'
              shape='round'
              size='large'
              onClick={() => handleInvitationGroupChange(invitationGroup)}
            >
              Book Slot
            </Button>
          )}
        </div>
      </div>

      <div className='sb-invitation-card__content'>
        {hasExpired && (
          <div className='sb-invitation-card__availability sb-invitation-card__availability--expired'>
            <IconItem text={`Expired - 0 Slots Available`} icon={<CloseCircleFilled />} />
          </div>
        )}

        {availableSlotCount > 0 && !hasExpired && (
          <div className='sb-invitation-card__availability'>
            <IconItem text={`${availableSlotCount} ${availableSlotCount === 1 ? 'Slot' : 'Slots'} Available`} icon={<CheckCircleFilled />} />
          </div>
        )}

        {availableSlotCount === 0 && !hasExpired && (
          <div className='sb-invitation-card__availability sb-invitation-card__availability--fully-booked'>
            <IconItem text={`Fully Booked`} icon={<CloseCircleFilled />} />
          </div>
        )}

        <div className='sb-invitation-card__date'>
          <IconItem text={sessionDateRange} icon={<CalendarOutlined />} />
        </div>

        <div className='sb-invitation-card__subject'>
          <IconItem text={lessonSubject} icon={<BookOutlined />} />
        </div>

        <div className='sb-invitation-card__student'>
          <IconItem text={studentName} icon={<UserOutlined />} />
        </div>

        {locationName && (
          <div className='sb-invitation-card__location'>
            <IconItem text={locationName} icon={meetingTypes.VENUE.icon} />
          </div>
        )}

        {groupLessonNote !== null && (
          <div className='sb-invitation-card__notes'>
            <IconItem text='Notes from the teacher' icon={<FormOutlined />} />
            <Paragraph ellipsis={{ rows: 2, expandable: true, symbol: 'more' }}>{groupLessonNote.note}</Paragraph>
          </div>
        )}

        {/** Book button for small screens */}
        {showBookButton && availableSlotCount === 0 && isWaitingListEnabled && joinWaitListButton(true)}

        {showBookButton && availableSlotCount > 0 && (
          <Button
            className='sb-invitation-card__book-button--mobile'
            type='primary'
            shape='round'
            size='large'
            onClick={() => handleInvitationGroupChange(invitationGroup)}
            block
          >
            Book Slot
          </Button>
        )}
      </div>
    </div>
  )
}

InvitationCard.propTypes = {
  event: PropTypes.object.isRequired,
  handleInvitationGroupChange: PropTypes.func.isRequired,
  handleShowWaitList: PropTypes.func.isRequired,
  invitation: PropTypes.object.isRequired,
  invitationGroup: PropTypes.object.isRequired,
  isWaitingListEnabled: PropTypes.bool.isRequired,
  joinedWaitinglist: PropTypes.bool.isRequired,
  session: PropTypes.object.isRequired
}

export default InvitationCard
